import React, { useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faShieldHalved } from "@fortawesome/free-solid-svg-icons";
import "../Modals/EditorModals/CookieConsentBanner.css";
import { useViewerCopy } from "../../utils/viewerLanguage";
import { useCookieConsentState } from "../../hooks/useCookieChoiceMade";

const DRAG_THRESHOLD = 5;
const EDGE_GAP = 16;

const CookiePreferencesButton = ({ position = "left", onPositionChange }) => {
  const viewerLanguage = useViewerCopy();
  const { choiceMade, acceptAll } = useCookieConsentState();
  const label = viewerLanguage.copy?.consent?.title || "Cookie and Privacy Preferences";
  const draggable = !!onPositionChange;
  const [side, setSide] = useState(position);
  const [dragLeft, setDragLeft] = useState(null);
  const [snapping, setSnapping] = useState(false);
  const drag = useRef(null);
  const suppressClick = useRef(false);

  const moveTo = (next) => {
    if (next === side) return;
    setSide(next);
    onPositionChange(next);
  };

  const handlePointerDown = (event) => {
    if (!draggable || event.button !== 0) return;
    const rect = event.currentTarget.getBoundingClientRect();
    drag.current = { startX: event.clientX, offset: event.clientX - rect.left, moved: false };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event) => {
    const current = drag.current;
    if (!current) return;
    if (!current.moved && Math.abs(event.clientX - current.startX) < DRAG_THRESHOLD) return;
    current.moved = true;
    const maxLeft = document.documentElement.clientWidth - event.currentTarget.offsetWidth - EDGE_GAP;
    setDragLeft(Math.min(Math.max(event.clientX - current.offset, EDGE_GAP), maxLeft));
  };

  const handlePointerUp = (event) => {
    const current = drag.current;
    drag.current = null;
    if (!current?.moved) return;
    suppressClick.current = true;
    const viewportWidth = document.documentElement.clientWidth;
    const next = event.clientX < viewportWidth / 2 ? "left" : "right";
    const target = next === "left" ? EDGE_GAP : viewportWidth - event.currentTarget.offsetWidth - EDGE_GAP;
    if (target === dragLeft) {
      setDragLeft(null);
    } else {
      setSnapping(true);
      setDragLeft(target);
    }
    moveTo(next);
  };

  const handleTransitionEnd = () => {
    if (!snapping) return;
    setSnapping(false);
    setDragLeft(null);
  };

  const handleClick = () => {
    if (suppressClick.current) {
      suppressClick.current = false;
      return;
    }
    window.dispatchEvent(new CustomEvent("openCookieConsentModal"));
  };

  const handleKeyDown = (event) => {
    if (!draggable) return;
    if (event.key === "ArrowLeft") moveTo("left");
    if (event.key === "ArrowRight") moveTo("right");
  };

  const classes = [
    "kz-privacy-button",
    choiceMade && !acceptAll ? "kz-privacy-button--nudge" : "",
    side === "right" ? "kz-privacy-button--right" : "",
    draggable ? "kz-privacy-button--draggable" : "",
    dragLeft !== null ? "kz-privacy-button--dragging" : "",
    snapping ? "kz-privacy-button--snapping" : "",
  ].filter(Boolean).join(" ");

  return (
    <button
      type="button"
      className={classes}
      style={dragLeft !== null ? { left: dragLeft, right: "auto" } : undefined}
      aria-label={label}
      title={draggable ? `${label} (drag or use arrow keys to move)` : label}
      onClick={handleClick}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={() => { drag.current = null; setDragLeft(null); }}
      onTransitionEnd={handleTransitionEnd}
      onKeyDown={handleKeyDown}
    >
      <FontAwesomeIcon icon={faShieldHalved} />
    </button>
  );
};

export default CookiePreferencesButton;
